<section class="user-details audit-details">
	<!-- X close button -->
	<div class="user-details__close" (click)="closePanel()">
		<mat-icon [svgIcon]="'icon-close'"></mat-icon>
	</div>

	<!-- ─── Zone A: Entity Header ──────────────────────────────────────── -->
	<div class="user-details__card audit-details__entity-card">
		<div class="audit-details__entity-head">Entity</div>

		<div class="audit-details__entity-row">
			<div class="audit-details__entity-id">{{ panelData().entityId }}</div>
			@if (!isEntityUnavailable()) {
				<span class="user-details__tag user-details__tag--status-pill" [ngClass]="panelData().actionClass">
					{{ panelData().actionLabel }}
				</span>
			}
		</div>

		@if (isEntityUnavailable()) {
			<!-- Degraded state: entity deleted or no longer retrievable -->
			<div class="audit-details__unavailable">Record unavailable</div>
		} @else {
			<div class="audit-details__entity-meta">
				<span class="lead-pill">
					<mat-icon [svgIcon]="'lead-icon'"></mat-icon>
					<span>{{ panelData().entityType || 'Lead' }}</span>
				</span>
				<span class="audit-details__entity-owner">{{ panelData().entityOwner }}</span>
			</div>
		}
	</div>

	<!-- ─── Zone B: Actor & Metadata Grid ─────────────────────────────── -->
	<div class="user-details__grid user-details__grid--two">
		<div class="user-details__field">
			<mat-icon class="user-details__field-icon actor-grid" svgIcon="icon-user-outline"></mat-icon>
			<div class="user-details__field-body">
				<div class="user-details__field-label">Actor</div>
				<div class="user-details__field-value">{{ panelData().actorName }}</div>
			</div>
			<span class="user-details__tag user-details__tag--neutral">{{ panelData().actorRole }}</span>
		</div>

		<div class="user-details__field">
			<mat-icon class="user-details__field-icon" svgIcon="icon-calendar"></mat-icon>
			<div class="user-details__field-body">
				<div class="user-details__field-label">Timeframe</div>
				<div class="user-details__field-value">{{ panelData().timeframe }}</div>
			</div>
		</div>

		<div class="user-details__field">
			<mat-icon class="user-details__field-icon" svgIcon="icon-globe"></mat-icon>
			<div class="user-details__field-body">
				<div class="user-details__field-label">IP Address</div>
				<div class="user-details__field-value user-details__field-value--lookup">{{ panelData().ipAddress }}</div>
			</div>
		</div>

		<div class="user-details__field">
			<mat-icon class="user-details__field-icon" svgIcon="icon-list-square"></mat-icon>
			<div class="user-details__field-body">
				<div class="user-details__field-label">Screen</div>
				<div class="user-details__field-value">{{ panelData().screen }}</div>
			</div>
		</div>
	</div>

	<!-- ─── Zone C: Change Details ────────────────────────────────────── -->
	<div class="user-details__section-header">
		<mat-icon class="user-details__section-icon" svgIcon="requote_icon"></mat-icon>
		<div class="user-details__section-title">Change Details</div>
	</div>

	<div class="user-details__field audit-details__attribute">
		<mat-icon class="user-details__field-icon" svgIcon="audit-trail-attribute"></mat-icon>
		<div class="user-details__field-body">
			<div class="user-details__field-label">Attribute</div>
			<div class="user-details__field-value">{{ panelData().attribute }}</div>
		</div>
	</div>

	<div class="audit-details__change-row">
		<div class="user-details__field audit-details__value-card">
			<div class="user-details__field-body">
				<div class="user-details__field-label">Before</div>
				<div class="user-details__field-value audit-details__value--before">
					{{ getDisplayValue(panelData().before, 'before', expandedFields()) }}
				</div>
				@if (needsShowMore(panelData().before)) {
					<button class="audit-details__show-more" (click)="toggleFieldExpansion('before')">
						{{ expandedFields().has('before') ? 'Show less' : 'Show more' }}
					</button>
				}
			</div>
		</div>

		<mat-icon class="audit-details__arrow">arrow_forward</mat-icon>

		<div class="user-details__field audit-details__value-card">
			<div class="user-details__field-body">
				<div class="user-details__field-label">After</div>
				<div class="user-details__field-value audit-details__value--after">
					{{ getDisplayValue(panelData().after, 'after', expandedFields()) }}
				</div>
				@if (needsShowMore(panelData().after)) {
					<button class="audit-details__show-more" (click)="toggleFieldExpansion('after')">
						{{ expandedFields().has('after') ? 'Show less' : 'Show more' }}
					</button>
				}
			</div>
		</div>
	</div>

	<!-- ─── Zone D: Additional Details (system/rule-triggered events only) ─── -->
	@if (isSystemTriggered()) {
		<div class="user-details__section-header">
			<mat-icon class="user-details__section-icon" svgIcon="icon-document"></mat-icon>
			<div class="user-details__section-title">Additional Details</div>
		</div>

		<div class="user-details__card audit-details__notes">
			<div class="audit-details__note-item">
				<span class="audit-details__note-label">Reason:</span>
				<span class="audit-details__note-value">
					{{ getDisplayValue(panelData().reason, 'reason', expandedFields()) }}
				</span>
				@if (needsShowMore(panelData().reason)) {
					<button class="audit-details__show-more" (click)="toggleFieldExpansion('reason')">
						{{ expandedFields().has('reason') ? 'Show less' : 'Show more' }}
					</button>
				}
			</div>
			<div class="audit-details__note-item">
				<span class="audit-details__note-label">Inactivity Period:</span>
				<span class="audit-details__note-value">{{ panelData().inactivityPeriod }}</span>
			</div>
			<div class="audit-details__note-item">
				<span class="audit-details__note-label">Threshold Applied:</span>
				<span class="audit-details__note-value">{{ panelData().thresholdApplied }}</span>
			</div>
			<div class="audit-details__note-item">
				<span class="audit-details__note-label">Rule Reference:</span>
				<span class="audit-details__note-value">{{ panelData().ruleReference }}</span>
			</div>
			<div class="audit-details__note-item">
				<span class="audit-details__note-label">Policy Reference:</span>
				<span class="audit-details__note-value">{{ panelData().policyReference }}</span>
			</div>
			<div class="audit-details__note-item">
				<span class="audit-details__note-label">Trigger Condition:</span>
				<span class="audit-details__note-value">
					{{ getDisplayValue(panelData().triggerCondition, 'triggerCondition', expandedFields()) }}
				</span>
				@if (needsShowMore(panelData().triggerCondition)) {
					<button class="audit-details__show-more" (click)="toggleFieldExpansion('triggerCondition')">
						{{ expandedFields().has('triggerCondition') ? 'Show less' : 'Show more' }}
					</button>
				}
			</div>
			<div class="audit-details__note-item">
				<span class="audit-details__note-label">Sensitive Change:</span>
				<span class="audit-details__note-value">{{ panelData().isSensitive }}</span>
			</div>
		</div>
	}

	<!-- ─── Zone E: Primary CTA (sticky footer) ───────────────────────── -->
	<div class="audit-details__footer">
		@if (isEntityUnavailable()) {
			<p class="audit-details__footer-note">Record unavailable</p>
		}
		<app-buttons
			label="View Full Lead History"
			variant="primary"
			[disabled]="isEntityUnavailable()"
			(clicked)="onViewFullLeadHistory()">
			<mat-icon btn-icon-start svgIcon="clock-refresh"></mat-icon>
		</app-buttons>
	</div>
</section>